import React, { useEffect, useState } from 'react';
import { Avatar } from 'src/components/atoms';
import {
  StyledIcon,
  StyledSubheader,
} from 'src/components/foundations/foundations.styles';
import { useTheme } from 'styled-components';
import { CasePermissionsType, FormInputVariant, FormState } from 'src/types';
import { returnDashIfNoValue } from 'src/utils';
import { getInitials } from 'src/utils/getInitials';
import { ButtonAndTooltip } from '../../ButtonAndTooltip';
import { InviteUserDropdownOption } from '../../DropdownSelect';
import { FormInput } from '../../FormInput';
import { NoHeaderTable } from '../../NoHeaderTable';
import { SearchFieldDescription } from '../InviteToCaseModal.styles';
import {
  AccessContainer,
  AccessName,
  AccessPermissions,
  HeaderContainer,
  SubheaderContainer,
} from './SummaryStep.styles';

interface Permissions {
  caseDetails: string;
  casePII: string;
  reportData: string;
  reportPII: string;
  reporterMessages: string;
  auditLogs: string;
  workspace: string;
}

interface Props {
  selectedTeamMembers: InviteUserDropdownOption[];
  selectedCaseAccess: CasePermissionsType[];
  note: string;
  handleNoteChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

const defaultPermissions: Permissions = {
  caseDetails: 'No access',
  casePII: 'Hide',
  reportData: 'No access',
  reportPII: 'Hide',
  reporterMessages: 'No access',
  auditLogs: 'No access',
  workspace: 'No access',
};

export const SummaryStep = ({
  selectedTeamMembers,
  selectedCaseAccess,
  note,
  handleNoteChange,
}: Props) => {
  const theme = useTheme();
  const [permissions, setPermissions] =
    useState<Permissions>(defaultPermissions);

  const displayPermissionAccess = (permission: CasePermissionsType) => {
    return permission.includes('VIEW') ? 'Read-only' : 'Editor';
  };

  useEffect(() => {
    const newPermissions = { ...defaultPermissions };

    const updatePermission = (
      accessType: CasePermissionsType,
      accessLevel: CasePermissionsType,
    ) => {
      switch (accessType) {
        case 'VIEW_CASE_DETAILS':
        case 'EDIT_CASE_DETAILS':
          newPermissions.caseDetails = displayPermissionAccess(accessLevel);
          break;
        case 'VIEW_CASE_DETAILS_PII':
          newPermissions.casePII = accessLevel.includes('VIEW')
            ? 'Show'
            : 'Hide';
          break;
        case 'VIEW_REPORT_DETAILS':
        case 'EDIT_REPORT_DETAILS':
          newPermissions.reportData = displayPermissionAccess(accessLevel);
          break;
        case 'VIEW_REPORT_DETAILS_PII':
          newPermissions.reportPII = accessLevel.includes('VIEW')
            ? 'Show'
            : 'Hide';
          break;
        case 'VIEW_CASE_AUDIT_LOGS_TAB':
        case 'EDIT_CASE_AUDIT_LOGS_TAB':
          newPermissions.auditLogs = displayPermissionAccess(accessLevel);
          break;
        case 'VIEW_CASE_WORKSPACE_TAB':
        case 'EDIT_CASE_WORKSPACE_TAB':
          newPermissions.workspace = displayPermissionAccess(accessLevel);
          break;
        case 'SEND_REPORT_MESSAGES':
        case 'VIEW_REPORT_MESSAGES':
          newPermissions.reporterMessages =
            displayPermissionAccess(accessLevel);
          break;
        default:
          break;
      }
    };

    selectedCaseAccess.forEach((caseAccess) => {
      updatePermission(caseAccess, caseAccess);
    });

    setPermissions(newPermissions);
  }, [selectedCaseAccess]);

  return (
    <>
      <HeaderContainer>
        <StyledSubheader>Review your invite</StyledSubheader>
        <ButtonAndTooltip
          buttonProps={{
            children: 'View case access',
            variant: 'secondary',
            'data-testid': 'view-case-access-button',
          }}
          tooltipProps={{
            children: (
              <>
                <AccessContainer>
                  <AccessName>Case details</AccessName>
                  <AccessPermissions>
                    {permissions.caseDetails}
                  </AccessPermissions>
                </AccessContainer>
                <AccessContainer>
                  <AccessName>Case PII</AccessName>
                  <AccessPermissions>{permissions.casePII}</AccessPermissions>
                </AccessContainer>
                <AccessContainer>
                  <AccessName>Report data</AccessName>
                  <AccessPermissions>
                    {permissions.reportData}
                  </AccessPermissions>
                </AccessContainer>
                <AccessContainer>
                  <AccessName>Report PII</AccessName>
                  <AccessPermissions>{permissions.reportPII}</AccessPermissions>
                </AccessContainer>
                <AccessContainer>
                  <AccessName>Reporter messages</AccessName>
                  <AccessPermissions>
                    {permissions.reporterMessages}
                  </AccessPermissions>
                </AccessContainer>
                <AccessContainer>
                  <AccessName>Audit logs</AccessName>
                  <AccessPermissions>{permissions.auditLogs}</AccessPermissions>
                </AccessContainer>
                <AccessContainer>
                  <AccessName>Workspace</AccessName>
                  <AccessPermissions>{permissions.workspace}</AccessPermissions>
                </AccessContainer>
              </>
            ),
            variant: 'up',
            width: '212px',
          }}
        />
      </HeaderContainer>
      <NoHeaderTable
        tableData={selectedTeamMembers.map((cm) => ({
          row: String(cm.value),
          cellData: [
            <Avatar
              variant="text"
              text={getInitials(cm.label)}
              key={`${cm.value}-avatar`}
              size="medium"
            />,
            cm.label || 'Unnamed user',
            returnDashIfNoValue(cm.role),
            returnDashIfNoValue(cm.site),
            returnDashIfNoValue(cm.country),
          ],
        }))}
        columns={[
          { label: 'Avatar' },
          { label: 'Name' },
          { label: 'Role' },
          { label: 'Site' },
          { label: 'Country' },
        ]}
        isFullWidth
        columnWidths={['56px', '25%', '25%', '25%', '25%']}
        data-testid="no-header-table"
      />
      <SubheaderContainer>
        <StyledIcon
          className="uil uil-link-alt"
          color={theme.atlas.semantic.color.type.default}
          size="24px"
        />
        <StyledSubheader>Attach a note</StyledSubheader>
      </SubheaderContainer>
      <SearchFieldDescription>
        Enhance the invite by adding a note to provide context for the
        invitation. This helps people to understand the reason they&apos;ve been
        invited to the case and will be included in their invite notification.
      </SearchFieldDescription>
      <FormInput
        variant={FormInputVariant.AREA}
        placeholder="Add a note (optional)"
        data-testid="summary-step-note"
        state={FormState.DEFAULT}
        onChange={handleNoteChange}
        defaultValue={note}
      />
    </>
  );
};
